import axios from "axios";
import { hardBaseUrl } from "services/urlConstant";

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ─── SFTP Download ────────────────────────────────────────────────────────────

/** List remote SFTP files for a given DSP slug and path */
export const listSFTPFiles = (dsp_slug, path = "", search = "") =>
  axios.get(`${hardBaseUrl}/dsp-sftp/list`, {
    headers: getAuthHeaders(),
    params: { dsp_slug, path, search },
  });

/** List DSP download options (paginated) */
export const listDspDownloadOptions = (page = 1, size = 10, search = "") =>
  axios.get(`${hardBaseUrl}/dsp-sftp/list-dsp-download`, {
    headers: getAuthHeaders(),
    params: { page, size, search },
  });

/** List local (Gluster) files & folders */
export const listLocalFiles = (dsp_slug, path = "", search = "") =>
  axios.get(`${hardBaseUrl}/dsp-sftp/local-list`, {
    headers: getAuthHeaders(),
    params: { dsp_slug, path, search },
  });

/** Sync SFTP cache for a DSP slug */
export const syncSFTP = dsp_slug =>
  axios.post(
    `${hardBaseUrl}/dsp-sftp/sync`,
    {},
    {
      headers: getAuthHeaders(),
      params: { dsp_slug },
    }
  );

/** Download files (batch) */
export const downloadFiles = payload =>
  axios.post(`${hardBaseUrl}/dsp-sftp/download`, payload, {
    headers: getAuthHeaders(),
  });

/** Retry a failed download */
export const retryDownload = payload =>
  axios.post(`${hardBaseUrl}/dsp-sftp/retry-download`, payload, {
    headers: getAuthHeaders(),
  });

/** Poll process status by process ID */
export const getProcessStatus = process_id =>
  axios.get(`${hardBaseUrl}/dsp-sftp/status/${process_id}`, {
    headers: getAuthHeaders(),
  });

// ─── DSP SFTP Configurations ──────────────────────────────────────────────────

/** List DSP master options for dropdown */
export const getDspOptions = (params = {}) => {
  let queryParams = {};
  if (typeof params === "object" && params !== null) {
    const page = params.page || 1;
    const limit = params.limit || params.per_page || params.size || 10;
    queryParams = {
      page,
      limit,
      per_page: limit,
      size: limit,
      offset:
        params.offset !== undefined ? params.offset : (page - 1) * limit,
      search: params.search || "",
      ...params,
    };
  } else {
    queryParams = {
      page: 1,
      limit: 10,
      per_page: 10,
      size: 10,
      offset: 0,
      search: params,
    };
  }
  return axios.get(`${hardBaseUrl}/dsp-sftp/config/dsp-options`, {
    headers: getAuthHeaders(),
    params: queryParams,
  });
};

/** List all DSP SFTP configs */
export const listDspSFTPConfigs = (params = {}, is_active = "", action = "") => {
  let queryParams = {};
  if (typeof params === "object" && params !== null) {
    queryParams = params;
  } else {
    queryParams = { search: params, is_active, action };
  }
  return axios.get(`${hardBaseUrl}/dsp-sftp/config/dsps`, {
    headers: getAuthHeaders(),
    params: queryParams,
  });
};

/** Get a single DSP SFTP config by ID */
export const getDspSFTPConfig = id =>
  axios.get(`${hardBaseUrl}/dsp-sftp/config/dsps/${id}`, {
    headers: getAuthHeaders(),
  });

/** Create a DSP SFTP config */
export const createDspSFTPConfig = payload =>
  axios.post(`${hardBaseUrl}/dsp-sftp/config/dsps`, payload, {
    headers: getAuthHeaders(),
  });

/** Update a DSP SFTP config */
export const updateDspSFTPConfig = (id, payload) =>
  axios.put(`${hardBaseUrl}/dsp-sftp/config/dsps/${id}`, payload, {
    headers: getAuthHeaders(),
  });

/** Delete a DSP SFTP config */
export const deleteDspSFTPConfig = id =>
  axios.delete(`${hardBaseUrl}/dsp-sftp/config/dsps/${id}`, {
    headers: getAuthHeaders(),
  });

/** Test DSP SFTP connection */
export const testDspSFTPConnection = (payload = {}) => {
  const body = typeof payload === "object" && payload !== null ? payload : {};
  return axios.post(`${hardBaseUrl}/dsp-sftp/config/dsps/test-connection`, body, {
    headers: getAuthHeaders(),
  });
};

// ─── SFTP App Configurations ──────────────────────────────────────────────────

/** List all SFTP app configs */
export const listSFTPAppConfigs = () =>
  axios.get(`${hardBaseUrl}/dsp-sftp/config/app-configs`, {
    headers: getAuthHeaders(),
  });

/** Update a SFTP app config */
export const updateSFTPAppConfig = (config_id, payload) =>
  axios.put(
    `${hardBaseUrl}/dsp-sftp/config/app-configs/${config_id}`,
    payload,
    { headers: getAuthHeaders() }
  );

// ─── Publisher Directories ────────────────────────────────────────────────────

/** List publisher SFTP directories */
export const listPublisherDirectories = (dsp_id, page = 1, size = 10, search = "") =>
  axios.get(`${hardBaseUrl}/dsp-sftp/directories`, {
    headers: getAuthHeaders(),
    params: { dsp_id, page, size, search },
  });

/** Batch save publisher SFTP directories */
export const savePublisherDirectories = payload =>
  axios.post(`${hardBaseUrl}/dsp-sftp/directories`, payload, {
    headers: getAuthHeaders(),
  });
